import type { TransformedLabelsField } from '@atlassian/jira-business-fields/src/common/field-types/labels/types.tsx';
import { useLabelsField } from '@atlassian/jira-issue-field-labels/src/services/labels-field-service/index.tsx';
import { LABELS_TYPE, LABELS_CF_TYPE } from '@atlassian/jira-platform-field-config';
import { createClipboardHelperComponent } from '../../../clipboard-handler/create-clipboard-helper-component';
import { useClipboardHandler } from '../../../clipboard-handler/use-clipboard-handler';
import {
	assertClipboardDataIsCompatible,
	assertClipboardHasPlainTextFormat,
} from '../../../clipboard-handler/utils';
import type {
	ClipboardItem,
	ListIssueFieldTransformed,
	ListIssueForCopyPaste,
	PasteHandler,
	PasteMetadata,
} from '../../../types';

const getPastedValue = (
	clipboardItem: ClipboardItem,
	metadata: PasteMetadata | null,
): string[] | null => {
	if (
		!assertClipboardHasPlainTextFormat(clipboardItem) ||
		!assertClipboardDataIsCompatible(metadata, [LABELS_TYPE, LABELS_CF_TYPE])
	) {
		return null;
	}

	return clipboardItem.plainText.split(',').map((label) => label.trim());
};

function assertLabelsField(
	field: ListIssueFieldTransformed,
): asserts field is TransformedLabelsField {
	if (!field) {
		throw new Error('Field is required');
	}

	if (field.type !== LABELS_TYPE && field.type !== LABELS_CF_TYPE) {
		throw new Error('Field type is not supported');
	}
}

function isNotNull(value: string | null): value is string {
	return value !== null;
}

const useLabelsClipboardHandler: PasteHandler = (
	issue: ListIssueForCopyPaste,
	field: ListIssueFieldTransformed,
) => {
	assertLabelsField(field);

	const [{ value }, { saveValue }] = useLabelsField({
		issueKey: issue.key,
		issueId: issue.issueId,
		fieldKey: field?.fieldId,
		initialValue: Array.isArray(field?.value) ? field.value.filter(isNotNull) : [],
	});

	return useClipboardHandler({
		issue,
		field,
		currentStringValue: value?.join(', ') || '',
		currentValue: value,
		getPastedValue,
		saveValue,
	});
};

const LabelsClipboardHelper = createClipboardHelperComponent(useLabelsClipboardHandler);

export const labelsClipboardHandler = {
	[LABELS_TYPE]: LabelsClipboardHelper,
	[LABELS_CF_TYPE]: LabelsClipboardHelper,
};
